<template>
	<div class="mw-100 rounded">
		<div v-if="isInCreateNewChecklistMode">
			<ChecklistEditor
				ref="newChecklistEditor"
				:save-to-project="handleNewChecklistCreated"
				:is-tab-view="true"
				:checklist-name="checklistSearch"
				:existing-names="projectChecklists.map(({ checklistName }) => checklistName)"
				@cancel="handleNewChecklistCancel"
				@update:isDirty="(dirty: boolean) => handleEditorDirtyChange('new-checklist', dirty)"
				@hide-lucky="hideLucky"
				@show-lucky="showLucky" />
		</div>
		<div v-else-if="projectChecklists.length === 0">
			<h6 class="mb-4">No checklists have been added to this project.</h6>
			<template v-if="showEditorActions">
				<v-btn
					v-if="showOnlyCreateNewChecklistBtn"
					append-icon="icon-plus"
					class="pa-2 text-none create-checklist-btn"
					color="transparent"
					@click="createNewChecklist">
					Create New Checklist
				</v-btn>
				<v-autocomplete
					v-else
					v-tooltip:top="addChecklistLabel"
					:aria-label="addChecklistLabel"
					v-model="selectedChecklistToAdd"
					v-model:search="checklistSearch"
					v-model:menu="localChecklistMenu"
					@update:modelValue="handleChecklistChange"
					@focus="checklistFieldFocus"
					@keydown.enter="handleSearchEnter"
					ref="addChecklistDropdown"
					:items="availableChecklists"
					item-title="name"
					item-value="id"
					variant="outlined"
					class="add-checklist-dropdown mb-0"
					:class="{ 'w-100': isSmallScreen }"
					auto-select-first
					:no-data-text="'No checklists found.'"
					:loading="checklistsLoading"
					no-filter
					density="compact"
					:list-props="{ class: 'pb-0' }"
					v-bind="searchFieldProps(checklistSearch)">
					<template #label><FieldLabel :label="addChecklistLabel" /></template>
					<template #append-item>
						<v-list-item
							v-if="canAddTemplates"
							class="create-checklist-fixed border-t border-solid pa-0"
							@click="createNewChecklist">
							<div class="text-center py-2 w-100">
								<v-btn class="pa-2 text-none create-checklist-btn" color="transparent">Create New Checklist</v-btn>
							</div>
						</v-list-item>
					</template>
					<template #append-inner>
						<v-icon v-if="canAddTemplates" color="primary" @click="createNewChecklist">icon-plus</v-icon>
					</template>
				</v-autocomplete>
			</template>
		</div>
		<div v-else>
			<div class="d-flex align-center w-100 ga-2 mb-2 px-2 px-md-6 checklist-header-row" :class="{ 'flex-wrap': isSmallScreen }">
				<span class="font-weight-medium flex-grow-0 flex-shrink-0 table-header checklist-status-col text-no-wrap">Completed</span>
				<span class="font-weight-medium flex-grow-0 flex-shrink-0 text-left table-header">Name</span>
				<div
					class="flex-grow-1 d-flex justify-end add-checklist-dropdown-wrap"
					:class="{ 'w-100 order-first': isSmallScreen && showEditorActions }">
					<v-autocomplete
						v-if="showEditorActions"
						v-tooltip:top="addChecklistLabel"
						:aria-label="addChecklistLabel"
						v-model="selectedChecklistToAdd"
						v-model:search="checklistSearch"
						v-model:menu="localChecklistMenu"
						@update:modelValue="handleChecklistChange"
						@focus="checklistFieldFocus"
						@keydown.enter="handleSearchEnter"
						ref="addChecklistDropdown"
						:items="availableChecklists"
						item-title="name"
						item-value="id"
						variant="outlined"
						density="compact"
						class="mb-0 add-checklist-dropdown"
						:class="{ 'w-100': isSmallScreen }"
						auto-select-first
						:no-data-text="'No checklists found.'"
						:loading="checklistsLoading"
						no-filter
						:list-props="{ class: 'pb-0' }"
						v-bind="searchFieldProps(checklistSearch)">
						<template #label><FieldLabel :label="addChecklistLabel" /></template>
						<template #append-item>
							<v-list-item
								v-if="canAddTemplates"
								class="create-checklist-fixed border-t border-solid pa-0"
								@click="createNewChecklist">
								<div class="text-center py-2 w-100">
									<v-btn class="pa-2 text-none create-checklist-btn" color="transparent"> Create New Checklist </v-btn>
								</div>
							</v-list-item>
						</template>
						<template #append-inner>
							<v-icon v-if="canAddTemplates" color="primary" @click="createNewChecklist">icon-plus</v-icon>
						</template>
					</v-autocomplete>
				</div>
			</div>

			<v-expansion-panels v-model="expandedPanelIndex" focusable flat>
				<v-expansion-panel v-for="(checklist, index) in projectChecklists" :key="checklist.checklistId">
					<v-expansion-panel-title
						class="expansion-panel-header px-2 px-md-6"
						:class="{ 'expansion-panel-header--ipad-landscape': shouldUseIpadLandscapeHeaderLayout(index) }"
						:hide-actions="isEditingChecklist(checklist)"
						:readonly="isEditingChecklist(checklist)">
						<InlineChecklistEditor
							v-if="isEditingChecklist(checklist)"
							ref="renameEditors"
							:checklist-id="checklist.checklistId"
							:name="checklist.checklistName"
							:existing-names="projectChecklists.map(({ checklistName }) => checklistName)"
							@update:isDirty="(dirty: boolean) => handleEditorDirtyChange(`rename:${checklist.checklistId}`, dirty)"
							@saved="(name: string) => handleChecklistNameSaved(checklist, name)"
							@gone="(error: unknown) => handleChecklistRenameGone(checklist.checklistId, error)"
							@cancel="cancelChecklistEdit" />
						<div
							v-else
							class="d-flex flex-grow-1 ga-2 min-w-0"
							:class="{
								'checklist-header--ipad-landscape': shouldUseIpadLandscapeHeaderLayout(index)
							}">
							<div
								class="d-flex align-center flex-grow-1 ga-2 min-w-0"
								:class="{ 'checklist-header-name-row--ipad-landscape': shouldUseIpadLandscapeHeaderLayout(index) }">
								<span class="checklist-status-col text-left text-no-wrap">{{ getCompletedItems(checklist) }}</span>
								<span
									:title="checklist.checklistName"
									class="flex-grow-1 text-left text-truncate checklist-name"
									:class="{ 'checklist-name--ipad-landscape': shouldUseIpadLandscapeHeaderLayout(index) }"
									>{{ checklist.checklistName }}</span
								>
							</div>
							<div
								v-if="isPanelExpanded(index) && showEditorActions"
								class="d-flex align-center flex-shrink-0"
								:class="{
									'w-100 justify-end checklist-remove-action--ipad-landscape': shouldUseIpadLandscapeHeaderLayout(index)
								}">
								<v-btn
									rounded="xl"
									variant="outlined"
									elevation="0"
									color="black"
									class="remove-checklist-btn"
									@click.stop="removeChecklistFromProject(checklist.checklistId)">
									Remove Checklist
								</v-btn>
							</div>
							<div
								class="d-flex align-center flex-shrink-0"
								:class="{ 'checklist-edit-action--ipad-landscape': shouldUseIpadLandscapeHeaderLayout(index) }">
								<v-btn
									v-if="showEditorActions"
									variant="plain"
									color="black"
									icon="icon-pencil-edit-stroke"
									density="compact"
									@click.stop="startChecklistEdit(checklist)">
								</v-btn>
							</div>
						</div>
					</v-expansion-panel-title>
					<v-progress-linear
						color="success"
						:model-value="getCompletedItemPercentage(checklist)"
						:chunk-count="shouldChunkProgressBar(checklist) ? checklist.items.length : null"></v-progress-linear>
					<v-expansion-panel-text
						class="expansion-panel-content"
						@dragover="onPanelDragover"
						@drop="(e: DragEvent) => onPanelDrop(checklist.checklistId, e)">
						<v-divider
							v-if="showDivider(checklist.checklistId, 0)"
							thickness="medium"
							color="primary"
							class="border-opacity-100"></v-divider>
						<template v-for="(item, itemIndex) in checklist.items" :key="item.checklistItemId">
							<div
								class="mb-3 project-checklist-item"
								:class="{ 'opacity-20': dragState.dragId === item.checklistItemId }"
								:data-checklist-id="checklist.checklistId"
								:data-checklist-item-id="item.checklistItemId"
								@dragover="(e: DragEvent) => onItemDragover(checklist.checklistId, item.checklistItemId, e)">
								<div class="d-flex align-center mb-2 checklist-item-row">
									<v-tooltip v-if="showEditorActions" :disabled="!!dragState.dragId" location="bottom">
										<template #activator="{ props }">
											<v-icon
												v-bind="props"
												:draggable="showEditorActions"
												class="cursor-grab"
												@dragstart="
													(e: DragEvent) => onHandleDragstart(checklist.checklistId, item.checklistItemId, e)
												"
												@dragend="onHandleDragend"
												@touchstart.prevent="() => onHandleTouchstart(checklist.checklistId, item.checklistItemId)"
												@touchmove="(e: TouchEvent) => onHandleTouchmove(checklist.checklistId, e)"
												@touchend="() => onHandleTouchend(checklist.checklistId)"
												@touchcancel="onHandleTouchcancel"
												>icon-grid-2x3</v-icon
											>
										</template>
										<span>Drag to reorder</span>
									</v-tooltip>
									<v-checkbox
										v-model="item.completed"
										:disabled="!showEditorActions"
										:hide-details="true"
										slim
										:class="{ 'completed-item-checkbox': item.completed }"
										@change="updateProjectChecklistItem(checklist.checklistId, item)">
										<template #label>
											<div class="d-flex flex-column">
												<span class="text-break">{{ item.checklistItemName }}</span>
												<v-label v-if="item.checklistItemRequiresMedia" class="text-red item-label"
													>(Requires a photo or video)</v-label
												>
											</div>
										</template>
									</v-checkbox>
									<v-spacer></v-spacer>
									<v-menu
										v-if="showEditorActions"
										ref="addContentMenu"
										location="bottom"
										close-on-content-click
										close-on-click
										@update:modelValue="onMenuToggle">
										<template #activator="{ props }">
											<v-btn
												v-test="'project-checklist-item-add-content'"
												color="primary"
												variant="outlined"
												elevation="0"
												rounded="xl"
												style="background-color: white"
												v-bind="props">
												Add Content
												<v-icon right size="small">{{
													props["aria-expanded"] === "true" ? "icon-chevron-up" : "icon-chevron-down"
												}}</v-icon>
											</v-btn>
										</template>
										<v-list>
											<v-list-item
												v-test="'project-checklist-item-from-project-media'"
												:disabled="mediaCount === 0"
												:class="{ 'disabled-list-item': mediaCount === 0, 'cursor-pointer': mediaCount > 0 }"
												prepend-icon="icon-image"
												@click="handleSelectMediaFromProject(checklist.checklistId, item.checklistItemId)">
												<v-list-item-title>From Project Media</v-list-item-title>
											</v-list-item>
											<v-list-item prepend-icon="icon-camera-solid" class="cursor-pointer">
												<v-list-item-title>Take Photos</v-list-item-title>
												<ActionButton
													:button-id="'fab-action-sync'"
													:project-id="projectId"
													:button-text="'Take Photos'"
													:button-icon="'icon-camera-solid'"
													:button-type="'textFab'"
													:action="'photo'"
													:project-checklist-item-id="item.id"
													style="
														position: absolute;
														opacity: 0;
														z-index: 1;
														width: 100%;
														height: 100%;
														top: 0;
														left: 0;
														cursor: pointer;
													"
													@close-parent-menu="closeMenu"></ActionButton>
											</v-list-item>
											<v-list-item prepend-icon="icon-recording-circle" class="cursor-pointer">
												<v-list-item-title>Start Recording</v-list-item-title>
												<ActionButton
													:button-id="'fab-action-sync'"
													:project-id="projectId"
													:button-text="'Start Recording'"
													:button-icon="'icon-recording-circle'"
													:button-type="'textFab'"
													:action="'async-recording'"
													:project-checklist-item-id="item.id"
													style="
														position: absolute;
														opacity: 0;
														z-index: 1;
														width: 100%;
														height: 100%;
														top: 0;
														left: 0;
														cursor: pointer;
													"
													@close-parent-menu="closeMenu"></ActionButton>
											</v-list-item>
											<v-list-item prepend-icon="icon-cloud-stroke-upload" class="cursor-pointer">
												<v-list-item-title>Upload From Device</v-list-item-title>
												<ActionButton
													:button-id="'fab-action-sync'"
													:project-id="projectId"
													:button-text="'Add Photos or Videos'"
													:button-icon="'icon-cloud-stroke-upload'"
													:button-type="'textFab'"
													:action="'upload-photo'"
													:allow-docs="false"
													:project-checklist-item-id="item.id"
													style="
														position: absolute;
														opacity: 0;
														z-index: 1;
														width: 100%;
														height: 100%;
														top: 0;
														left: 0;
														cursor: pointer;
													"
													@close-parent-menu="closeMenu"></ActionButton>
											</v-list-item>
										</v-list>
									</v-menu>
								</div>

								<div v-if="item.submissions && item.submissions.length > 0" class="mt-2">
									<div class="d-flex flex-wrap">
										<div
											v-for="submission in item.submissions"
											:key="submission.id"
											class="submission-item"
											@click="onItemClick(submission.mediaId)">
											<div class="submission-content">
												<v-img
													v-if="submission.mediaType === 'snapshot'"
													:lazy-src="getImageUrl(submission.media)"
													:src="getImageUrl(submission.media)"
													class="submission-image"
													cover>
												</v-img>
												<VideoPreview
													v-else-if="submission.mediaType === 'recording'"
													:thumbnail="submission.media.thumbnailUrl || ''"
													:parent-type="entityType" />
												<v-btn
													v-if="showEditorActions"
													icon
													class="submission-remove-btn"
													@click.stop="
														removeMedia(
															checklist.checklistId,
															item.checklistItemId,
															submission.mediaId,
															item,
															submission.mediaType,
															item.submissions.length === 1
														)
													">
													<v-icon size="small">icon-close-medium</v-icon>
												</v-btn>
											</div>
										</div>
									</div>
								</div>
							</div>
							<v-divider
								v-if="showDivider(checklist.checklistId, itemIndex + 1)"
								thickness="medium"
								color="primary"
								class="border-opacity-100"></v-divider>
						</template>
						<ChecklistItemEditor
							v-if="showEditorActions && !isExpectingMediaSelection"
							ref="itemEditors"
							:checklist-id="checklist.checklistId"
							:disabled="checklist.items.length >= maxChecklistItems"
							class="mt-3"
							@checklist:updated="handleChecklistUpdated"
							@gone="(error: unknown) => handleChecklistItemAddGone(checklist.checklistId, error)"
							@update:isDirty="(dirty: boolean) => handleEditorDirtyChange(`item:${checklist.checklistId}`, dirty)"
							@hide-lucky="hideLucky"
							@show-lucky="showLucky" />
					</v-expansion-panel-text>
				</v-expansion-panel>
			</v-expansion-panels>
		</div>

		<v-dialog v-model="lightBoxOpen" content-class="no-scroll" fullscreen persistent no-click-animation>
			<MediaLightBox
				ref="mediaLightBox"
				v-model="lightboxDocuments"
				v-test="'checklists-artifacts-lightbox'"
				:me="me"
				:is-view-only="isViewOnly"
				:has-free-trial-ended="hasFreeTrialEnded"
				:parent-type="'checklist'"
				:is-external-editor="isExternalEditor"
				:external-access-resolved="externalAccessResolved"
				:project-id="projectId"
				:project="project"
				:project-access-denied-handled-globally="true"
				@share="share"
				@close-lightbox="closeLightBox"
				@load-recordings="loadTimeline" />
		</v-dialog>
	</div>
</template>

<script lang="ts">
	import { searchFieldProps, isSearchTooLong as exceedsSearchLimit } from "@/helpers/searchLimit";
	import api from "@/api";
	import Checklist from "@/types/Checklist";
	import { ProjectChecklistsResponse, ProjectChecklistItemsResponse, ProjectChecklistItemMedia } from "@/types/ProjectChecklistsResponse";
	import ChecklistEditor from "@/components/ChecklistEditor.vue";
	import { UNSAVED_CHANGES_DIALOG_OPTIONS, type ConfirmDialogApi } from "@/composables/useUnsavedChangesGuard";
	import ChecklistItemEditor from "@/components/ChecklistItemEditor.vue";
	import InlineChecklistEditor from "./InlineChecklistEditor.vue";
	import ActionButton from "@/components/ActionButton.vue";
	import { calculateTimeout, getMessageFromError } from "@/helpers/utilities";
	import { toSplicedCompat } from "@/helpers/arrayCompat";
	import VideoPreview from "@/components/common/VideoPreview.vue";
	import MediaLightBox from "@/components/MediaLightBox.vue";
	import { EntityType } from "@/types/EntityType";
	import { SearchRequest } from "@/types/search/SearchRequest";
	import { emptyEntitySearchResponse, type EntitySearchResponse } from "@/types/search/EntitySearchResponse";
	import { MAX_CHECKLIST_ITEMS } from "@/constants/checklist";
	import DeviceUtility from "@/helpers/device";
	import { nextTick, ref, watch } from "vue";
	import { useOrientationChange } from "@/composables/useOrientationChange";
	import { useDisplay } from "vuetify";
	import { useToast } from "vue-toastification";
	import type { IndexableDocument } from "@/types/search/IndexableDocument";
	import FieldLabel from "@common-ui/components/FieldLabel.vue";
	import { couldNot } from "@/helpers/userMessages";
	import { getApiErrorStatus, isEntityGoneError } from "@/helpers/apiErrorShape";

	type DragState = { checklistId: string | null; dragId: string | null } & (
		| { dropId: string; dropAt: "top" | "bottom" }
		| { dropId: null; dropAt: null }
	);
	const ADD_CHECKLIST_LABEL = "Pick Checklist (Optional)";

	function getItemIndex(checklists: ProjectChecklistsResponse[], checklistId: string, checklistItemId: string) {
		const checklist = checklists.find((c) => c.checklistId === checklistId);
		if (!checklist) {
			return -1;
		}
		return checklist.items.findIndex((item) => item.checklistItemId === checklistItemId);
	}

	function getCompletedItemCount(checklist: ProjectChecklistsResponse) {
		return checklist.items.filter((item: ProjectChecklistItemsResponse) => item.completed).length;
	}

	const UNSAVED_CHECKLIST_DIALOG_TEXT = {
		title: "You have unsaved changes",
		message: "Your checklist hasn't been saved. Would you like to save it before leaving?",
		agreeText: "Save",
		cancelText: "Cancel"
	};

	export default {
		name: "ProjectChecklists",
		setup() {
			const toast = useToast();
			const isIpadDevice = DeviceUtility.isIpad;
			const isIpadLandscapeOrientation = ref(isIpadDevice && DeviceUtility.isLandscape);
			const { orientationChangeCount } = useOrientationChange();
			// Drives the responsive layout of the "Pick Checklist (Optional)" dropdown, which
			// needs the full row width on sm-and-down (phones and portrait tablets) so its label is not
			// truncated (LSP-20695). Same breakpoint as the .add-checklist-dropdown media query below.
			const { smAndDown } = useDisplay();

			watch(orientationChangeCount, () => {
				isIpadLandscapeOrientation.value = isIpadDevice && DeviceUtility.isLandscape;
			});

			return { addChecklistLabel: ADD_CHECKLIST_LABEL, isIpadLandscapeOrientation, isSmallScreen: smAndDown, toast };
		},
		components: {
			FieldLabel,
			ChecklistEditor,
			ChecklistItemEditor,
			InlineChecklistEditor,
			ActionButton,
			VideoPreview,
			MediaLightBox
		},
		props: {
			isExternalEditor: {
				type: Boolean,
				default: false
			},
			externalAccessResolved: {
				type: Boolean,
				default: true
			},
			projectId: {
				type: String,
				required: true
			},
			canAddTemplates: {
				type: Boolean,
				required: true
			},
			isViewOnly: {
				type: Boolean,
				required: true
			},
			isAuthenticated: {
				type: Boolean,
				required: true
			},
			hasFreeTrialEnded: {
				type: Boolean,
				required: true
			},
			mediaCount: {
				type: Number,
				default: 0
			},
			me: {
				type: Object,
				default: null,
				required: false
			},
			project: {
				type: Object,
				default: null,
				required: false
			},
			checklistMenu: {
				type: Boolean,
				default: false
			},
			// LSP-15740: when set, the viewer reached this project through a
			// shared-link URL (`?shareId=...`). For unauthenticated viewers, that
			// shareId lets us call the public checklist endpoint instead of the
			// authenticated one, which would otherwise 401 and bounce them to
			// login. Authenticated viewers ignore it and use the standard route.
			shareId: {
				type: String,
				default: ""
			}
		},
		emits: [
			"select-media-from-project",
			"cancel-select-media-from-project",
			"item-click",
			"share",
			"conversation-created",
			"load-timeline",
			"hide-lucky",
			"show-lucky",
			"update:checklistMenu"
		],
		data() {
			return {
				maxChecklistItems: MAX_CHECKLIST_ITEMS,
				projectChecklists: [] as ProjectChecklistsResponse[],
				availableChecklists: [] as Checklist[],
				hasAnyChecklists: true,
				addingChecklist: false,
				expandedPanelIndex: null,
				selectedChecklistToAdd: null as string | null,
				checklistSearch: "",
				checklistsLoading: false,
				isInCreateNewChecklistMode: false,
				currentChecklistId: "",
				currentItemId: "",
				// Review round 2 (cursor): loadProjectChecklists has no request sequencing, and
				// cancelMediaSelectionIfTargetMissing now runs on every reload — so a late-resolving OLDER
				// response, one that predates the target item's creation, could be read as proof the target
				// is gone and tear down the user's in-progress selection, discarding media they had already
				// picked. Widening the guard to the ITEM (this round's finding #2) made that materially more
				// likely, since an older response is far likelier to be missing a just-created item than a
				// whole checklist. This token gates ONLY the cancel decision: a stale response may still
				// overwrite the list (pre-existing, repaired by the next reload) but can never destroy work.
				checklistReloadSequence: 0,
				isExpectingMediaSelection: false,
				// LSP-17252 break-it round 2: bumped on every meaningful change to the three fields
				// above (handleSelectMediaFromProject starting a flow, clearExpectedMediaSelection
				// ending one) so an in-flight handleSelectedMedia call can detect it captured a now-
				// stale target and skip mutating/reporting against whatever flow is active now.
				mediaSelectionGeneration: 0,
				checklistMenuWidth: "0px",
				mediaResponse: emptyEntitySearchResponse() as EntitySearchResponse,
				lightBoxOpen: false,
				currentLightBoxId: null as string | null,
				dirtyEditors: new Set<string>(),
				pendingReload: false,
				searchRequest: null as SearchRequest | null,
				editingChecklistId: null as string | null,
				dragState: {
					checklistId: null,
					dragId: null,
					dropId: null,
					dropAt: null
				} as DragState
			};
		},
		computed: {
			entityType(): EntityType {
				return "project";
			},
			showEditorActions(): boolean {
				return !!this.isAuthenticated && !this.isViewOnly && !this.hasFreeTrialEnded;
			},
			lightboxDocuments: {
				get() {
					return this.mediaResponse.documents.filter((document) => document.id === this.currentLightBoxId);
				},
				set(newValue: IndexableDocument[]) {
					this.mediaResponse.documents = this.mediaResponse.documents.map(
						(document) => newValue.find(({ id }) => id === document.id) ?? document
					);
				}
			},
			localChecklistMenu: {
				get() {
					return this.checklistMenu;
				},
				set(newValue: boolean) {
					this.$emit("update:checklistMenu", newValue);
				}
			},
			showOnlyCreateNewChecklistBtn() {
				return !this.checklistsLoading && !this.hasAnyChecklists && this.canAddTemplates;
			},
			dragIndex() {
				return this.dragState.checklistId && this.dragState.dragId
					? getItemIndex(this.projectChecklists, this.dragState.checklistId, this.dragState.dragId)
					: -1;
			},
			dropIndex() {
				if (!this.dragState.checklistId || !this.dragState.dropId) {
					return -1;
				}
				let dropIndex = getItemIndex(this.projectChecklists, this.dragState.checklistId, this.dragState.dropId);
				if (dropIndex < 0) {
					return -1;
				}
				if (this.dragState.dropAt === "bottom") {
					dropIndex++;
				}
				return dropIndex;
			}
		},
		watch: {
			async checklistSearch(val) {
				clearTimeout(this._searchTimerId);
				if (exceedsSearchLimit(val)) {
					this.checklistsLoading = false;
					return;
				}
				this.checklistsLoading = true;
				val = val?.trim();
				this._searchTimerId = setTimeout(async () => {
					await this.performChecklistSearch(val);
				}, 500);
			}
		},
		async mounted() {
			window.addEventListener("resize", this.adjustChecklistMenuWidth);
			try {
				if (this.isAuthenticated) {
					const { rows = [] } = await api.getChecklists({
						page: 1,
						pageSize: 1,
						order: [["createdAt", "DESC"]]
					});
					this.hasAnyChecklists = rows.length > 0;
				}
			} catch (error) {
				console.error(error, {
					extra: {
						file: "ProjectChecklists.vue",
						method: "mounted"
					},
					tags: {
						projectId: this.projectId
					}
				});
			}
			await this.loadProjectChecklists();
			this.adjustChecklistMenuWidth();
		},
		beforeUnmount() {
			window.removeEventListener("resize", this.adjustChecklistMenuWidth);
		},
		methods: {
			searchFieldProps,
			shouldUseIpadLandscapeHeaderLayout(index: number): boolean {
				return this.isIpadLandscapeOrientation && this.isPanelExpanded(index) && this.showEditorActions;
			},
			getCompletedItemPercentage(checklist: ProjectChecklistsResponse) {
				return checklist.items.length > 0 ? (getCompletedItemCount(checklist) / checklist.items.length) * 100 : 0;
			},
			getCompletedItems(checklist: ProjectChecklistsResponse) {
				return getCompletedItemCount(checklist) + " of " + checklist.items.length;
			},
			shouldChunkProgressBar(checklist: ProjectChecklistsResponse) {
				const total = checklist.items.length;
				return total > 1 && getCompletedItemCount(checklist) < total;
			},

			isPanelExpanded(index: number) {
				return this.expandedPanelIndex === index;
			},
			isEditingChecklist(checklist: ProjectChecklistsResponse) {
				return this.editingChecklistId === checklist.checklistId;
			},
			startChecklistEdit(checklist: ProjectChecklistsResponse) {
				if (this.isEditingChecklist(checklist)) {
					return;
				}
				this.editingChecklistId = checklist.checklistId;
			},
			cancelChecklistEdit() {
				this.editingChecklistId = null;
			},
			handleChecklistNameSaved(checklist: ProjectChecklistsResponse, updatedName: string) {
				checklist.checklistName = updatedName;
				if (this.isEditingChecklist(checklist)) {
					this.cancelChecklistEdit();
				}
			},
			// LSP-20291: a checklist can be removed from the project on another device (e.g. the iPad app)
			// while this page still shows it. Any action on it then 404s/410s. Instead of a misleading
			// "could not ..." error, refresh the checklists so it goes away and tell the user it was already
			// deleted. Returns false when the error is not an "entity gone" error, or when the checklist is
			// still there after the refresh (the 404 was about something else, e.g. an item), so the caller
			// shows its usual error.
			async handleChecklistAlreadyDeleted(checklistId: string, action: string, error: unknown): Promise<boolean> {
				if (!isEntityGoneError(error)) {
					return false;
				}
				if (this.editingChecklistId === checklistId) {
					this.cancelChecklistEdit();
				}
				await this.loadProjectChecklists();
				const stillPresent = this.projectChecklists.some(
					(checklist: ProjectChecklistsResponse) => checklist.checklistId === checklistId
				);
				console.warn("ProjectChecklists.handleChecklistAlreadyDeleted: action hit a missing checklist", {
					projectId: this.projectId,
					checklistId,
					action,
					httpStatus: getApiErrorStatus(error),
					stillPresentAfterRefresh: stillPresent
				});
				if (stillPresent) {
					return false;
				}
				const alreadyDeletedMessage = "This checklist has already been deleted.";
				this.toast.warning(alreadyDeletedMessage, { timeout: calculateTimeout(alreadyDeletedMessage) });
				return true;
			},
			async handleChecklistItemAddGone(checklistId: string, error: unknown) {
				if (!(await this.handleChecklistAlreadyDeleted(checklistId, "add-item", error))) {
					const message = getMessageFromError(error) || "There was an error creating the checklist item. Please try again.";
					this.toast.error(message, { timeout: calculateTimeout(message) });
				}
			},
			async handleChecklistRenameGone(checklistId: string, error: unknown) {
				if (!(await this.handleChecklistAlreadyDeleted(checklistId, "rename", error))) {
					this.toast.error(couldNot("update the checklist name"));
				}
			},
			async loadProjectChecklists() {
				const reloadToken = ++this.checklistReloadSequence;
				try {
					// LSP-15740: unauthenticated visitors with a shareId go through
					// the public route, which validates the share gate server-side
					// and returns a PII-stripped read-only response. Otherwise the
					// authenticated route is used.
					if (!this.isAuthenticated && this.shareId) {
						this.projectChecklists = await api.getProjectChecklistsInsecure(this.projectId, this.shareId);
					} else {
						this.projectChecklists = await api.getProjectChecklists(this.projectId, !this.isAuthenticated);
					}
					// LSP-17252 review round: removeChecklistFromProject only cancels an active
					// media-selection flow when THIS client removes its target — a concurrent removal
					// by ANOTHER user (or session) never goes through that method, so the flow would
					// keep targeting a checklist the server no longer has, reproducing the original
					// stale-button bug via a different actor. Every reload observes server truth, so
					// this is the one place that catches it regardless of who removed it.
					// Review round 2 (cursor): only the NEWEST reload may cancel. An older response cannot
					// prove the target is gone — it may simply predate the target's creation.
					if (reloadToken === this.checklistReloadSequence) {
						this.cancelMediaSelectionIfTargetMissing("reload");
					} else if (this.isExpectingMediaSelection) {
						// Logged rather than silently skipped: this is the branch that protects in-progress
						// work, so a future "the flow got torn down anyway" report needs to show it ran.
						console.warn(
							"ProjectChecklists.loadProjectChecklists: a superseded reload will not cancel the active media-selection flow",
							{
								projectId: this.projectId,
								reloadToken,
								currentReloadToken: this.checklistReloadSequence,
								currentChecklistId: this.currentChecklistId,
								currentItemId: this.currentItemId
							}
						);
					}
				} catch (error) {
					console.error(error, {
						extra: {
							file: "ProjectChecklists.vue",
							method: "loadProjectChecklists",
							hasShareId: !!this.shareId
						},
						tags: {
							projectId: this.projectId
						}
					});
					this.toast.error(couldNot("load checklists"));
				}
			},
			// Shared by loadProjectChecklists (a checklist removed by another actor/session vanishes
			// on the next reload) — cancels an in-progress media-selection flow if the checklist it is
			// targeting is no longer present in projectChecklists. removeChecklistFromProject has its
			// own inline version of this check (it already knows exactly which id it just removed and
			// logs a richer "was a flow active at all" diagnostic), so it does not call this helper —
			// this one is for every OTHER path that can observe a checklist vanishing.
			//
			// LSP-17252 review round 2 (ls-heroku-ci #2): the flow's target is the PAIR
			// (currentChecklistId, currentItemId) — handleSelectMediaFromProject is the only setter and
			// always sets both — but this only checked the checklist. ChecklistEditor's removeItem()
			// deletes a single item while leaving its parent checklist in place, so a flow targeting a
			// just-deleted item early-returned here and Timeline kept showing "Add to Checklist" for an
			// item that no longer exists: the exact bug this PR fixes at the checklist level, surviving
			// one level down. getItemIndex returns -1 for a missing checklist OR a missing item, so it
			// covers both in one check (same helper the drag handlers already use).
			cancelMediaSelectionIfTargetMissing(reason: string) {
				if (!this.isExpectingMediaSelection) {
					return;
				}
				const targetStillExists = getItemIndex(this.projectChecklists, this.currentChecklistId, this.currentItemId) >= 0;
				if (targetStillExists) {
					// Review round 3 (ls-heroku-ci #2): the sibling check inside removeChecklistFromProject logs
					// BOTH outcomes (cancellingSelection true/false) whenever a flow is active; this one logged
					// neither. Given how many times this bug has recurred (LSP-16140, then LSP-17252), a future
					// "the reload guard should have caught this" report needs to distinguish "ran and concluded
					// the target is present" from "never ran at all". Only reachable with a flow active (the
					// isExpectingMediaSelection early return is above), so it is bounded by user action.
					console.warn(
						"ProjectChecklists.cancelMediaSelectionIfTargetMissing: the targeted checklist item is still present — leaving the flow active",
						{
							projectId: this.projectId,
							targetChecklistId: this.currentChecklistId,
							targetItemId: this.currentItemId,
							cancellingSelection: false,
							reason
						}
					);
					return;
				}
				console.warn(
					"ProjectChecklists.cancelMediaSelectionIfTargetMissing: the checklist item an active media-selection flow was targeting is no longer present — cancelling the flow",
					{
						projectId: this.projectId,
						missingChecklistId: this.currentChecklistId,
						missingItemId: this.currentItemId,
						// Distinguishes "whole checklist gone" from "just this item gone" during triage.
						checklistStillPresent: this.projectChecklists.some(
							(checklist: ProjectChecklistsResponse) => checklist.checklistId === this.currentChecklistId
						),
						reason
					}
				);
				this.clearExpectedMediaSelection();
				this.$emit("cancel-select-media-from-project");
			},
			async performChecklistSearch(val: string) {
				if (exceedsSearchLimit(val)) {
					this.checklistsLoading = false;
					return;
				}
				try {
					const allChecklists = await api.getChecklists({
						page: 1,
						pageSize: 1000,
						order: [["createdAt", "DESC"]],
						where: {
							name: { $ilike: `%${val ?? ""}%` }
						}
					});
					this.availableChecklists = allChecklists.rows;
				} catch (error) {
					console.error(error, {
						extra: {
							file: "ProjectChecklists.vue",
							method: "performChecklistSearch"
						},
						tags: {
							projectId: this.projectId,
							searchTerm: val
						}
					});
					this.toast.error(couldNot("search checklists"));
				} finally {
					this.checklistsLoading = false;
				}
			},

			async addChecklistToProject(checklistId?: string | null) {
				const resolvedChecklistId = checklistId ?? this.selectedChecklistToAdd;
				if (!resolvedChecklistId) {
					return false;
				}

				this.addingChecklist = true;
				try {
					await api.createProjectChecklist(this.projectId, resolvedChecklistId);
					this.toast.success("Checklist added to project.");
					this.selectedChecklistToAdd = null;
					this.$nextTick(() => {
						//@ts-expect-error
						this.$refs.addChecklistDropdown?.blur();
					});
					await this.loadProjectChecklists();
					return true;
				} catch (error) {
					console.error(error, {
						extra: {
							file: "ProjectChecklists.vue",
							method: "addChecklistToProject"
						},
						tags: {
							projectId: this.projectId,
							checklistId: this.selectedChecklistToAdd
						}
					});
					this.toast.error(getMessageFromError(error, couldNot("add a checklist to the project")));
					return false;
				} finally {
					this.addingChecklist = false;
				}
			},
			handleChecklistChange(value: string | null) {
				this.addChecklistToProject(value);
			},

			async removeChecklistFromProject(checklistId: string) {
				try {
					if (
						await (this as any).$root.$confirm.open(
							"Remove Checklist",
							"Are you sure you want to remove this checklist from the project?",
							false,
							"Remove",
							"Cancel",
							true
						)
					) {
						await api.deleteProjectChecklist(this.projectId, checklistId);
						this.toast.success("Checklist removed from project.");
						this.projectChecklists = this.projectChecklists.filter(
							(c: ProjectChecklistsResponse) => c.checklistId !== checklistId
						);

						// LSP-17252: a media-selection flow (Timeline's "Add to Checklist" button + this
						// component's isExpectingMediaSelection) can still be targeting a checklist that just
						// got removed. When a flow IS active, log which checklist it targeted and whether
						// THIS removal is the one cancelling it, so a future "stale Add to Checklist button"
						// report is traceable — removing a checklist while no flow is active logs nothing;
						// that is the ordinary, non-bug case, and logging every removal regardless of
						// relevance would be noise rather than a diagnostic. Only cancel when the removed
						// checklist is the one actually being targeted — removing a DIFFERENT checklist must
						// never cancel an in-progress selection for another one.
						if (this.isExpectingMediaSelection) {
							const isActiveSelectionTarget = this.currentChecklistId === checklistId;
							console.warn(
								"ProjectChecklists.removeChecklistFromProject: a checklist media-selection flow was active during removal",
								{
									projectId: this.projectId,
									removedChecklistId: checklistId,
									activeSelectionChecklistId: this.currentChecklistId,
									activeSelectionItemId: this.currentItemId,
									cancellingSelection: isActiveSelectionTarget
								}
							);
							if (isActiveSelectionTarget) {
								this.clearExpectedMediaSelection();
								// Tell the parent (EntityDetailPage) to exit Timeline's checklist-selection mode
								// too — clearExpectedMediaSelection alone only resets OUR local bookkeeping; the
								// visible "Add to Checklist" button lives entirely in Timeline's own selecting/
								// selectingChecklistMedia state (see Timeline.vue's cancelChecklistSelection).
								this.$emit("cancel-select-media-from-project");
							}
						}
					}
				} catch (error) {
					if (await this.handleChecklistAlreadyDeleted(checklistId, "remove", error)) {
						return;
					}
					console.error(error, {
						extra: {
							file: "ProjectChecklists.vue",
							method: "removeChecklistFromProject"
						},
						tags: {
							projectId: this.projectId,
							checklistId: checklistId
						}
					});
					this.toast.error(couldNot("remove the checklist from the project"));
				}
			},
			async updateProjectChecklistItem(checklistId: string, item: ProjectChecklistItemsResponse) {
				try {
					// Only validate media requirement when marking item as completed
					if (item.completed && item.checklistItemRequiresMedia) {
						if (!item.submissions || item.submissions.length === 0) {
							item.completed = false;
							this.toast.error("This item requires at least one photo or video to be completed.");
							return;
						}
					}
					await api.updateProjectChecklistItem(this.projectId, checklistId, item);
					this.toast.success("Checklist item updated.");
				} catch (error) {
					item.completed = !item.completed;
					if (await this.handleChecklistAlreadyDeleted(checklistId, "update-item", error)) {
						return;
					}
					console.error(error, {
						extra: {
							file: "ProjectChecklists.vue",
							method: "updateProjectChecklistItem"
						},
						tags: {
							projectId: this.projectId,
							checklistId: checklistId,
							itemId: item.checklistItemId
						}
					});
					this.toast.error("We could not update the checklist item. Please try again.");
				}
			},
			async removeMedia(
				checklistId: string,
				itemId: string,
				mediaId: string,
				item: ProjectChecklistItemsResponse,
				mediaType: string,
				isLastMedia: boolean
			) {
				try {
					const requiresMediaAndCompleted = item.checklistItemRequiresMedia && item.completed;

					let confirmMessage = "Are you sure you want to remove this media from the checklist?";
					if (isLastMedia && requiresMediaAndCompleted) {
						const mediaTypeLabel = mediaType === "snapshot" ? "photo" : "video";
						confirmMessage = `This item requires at least one photo or video to be completed. If you delete this ${mediaTypeLabel} we will update this item to be not completed.`;
					}

					if (await (this as any).$root.$confirm.open("Remove Media", confirmMessage, false, "Remove", "Cancel", true)) {
						await api.deleteProjectChecklistItemMedia(this.projectId, checklistId, itemId, mediaId);
						this.toast.success("Media removed.");

						// If this was the last media from a completed item that requires media, update status
						if (isLastMedia && requiresMediaAndCompleted) {
							try {
								item.completed = false;
								await api.updateProjectChecklistItem(this.projectId, checklistId, item);
							} catch (error) {
								console.error(error, {
									extra: {
										file: "ProjectChecklists.vue",
										method: "removeMedia"
									},
									tags: {
										projectId: this.projectId,
										checklistId: checklistId,
										itemId: itemId
									}
								});
								this.toast.error("We could not update the checklist item. Please try again.");
								item.completed = true;
							}
						}
					}
				} catch (error) {
					if (await this.handleChecklistAlreadyDeleted(checklistId, "remove-media", error)) {
						return;
					}
					console.error(error, {
						extra: {
							file: "ProjectChecklists.vue",
							method: "removeMedia"
						},
						tags: {
							projectId: this.projectId,
							checklistId: checklistId,
							itemId: itemId,
							mediaId: mediaId
						}
					});
					this.toast.error(couldNot("remove media"));
				} finally {
					await this.loadProjectChecklists();
				}
			},

			async createNewChecklist() {
				this.isInCreateNewChecklistMode = true;
			},

			resetDropdown(): void {
				this.checklistSearch = "";
				(this.$refs.addChecklistDropdown as any)?.blur();
			},

			async handleNewChecklistCreated(id: string): Promise<boolean> {
				if (!(await this.addChecklistToProject(id))) return false;
				this.localChecklistMenu = false;
				this.isInCreateNewChecklistMode = false;
				this.hasAnyChecklists = true;
				return true;
			},

			handleSearchEnter(event: KeyboardEvent) {
				if (this.availableChecklists.length === 0 && !this.checklistsLoading && this.canAddTemplates) {
					event.preventDefault();
					this.createNewChecklist();
				}
			},
			handleNewChecklistCancel() {
				this.isInCreateNewChecklistMode = false;
				this.resetDropdown();
			},

			handleEditorDirtyChange(editorId: string, isDirty: boolean) {
				if (isDirty) this.dirtyEditors.add(editorId);
				else this.dirtyEditors.delete(editorId);
			},

			hasUnsavedChecklist(): boolean {
				return this.dirtyEditors.size > 0;
			},

			async confirmLeaveWithUnsavedChecklist(): Promise<boolean> {
				if (!this.hasUnsavedChecklist()) return true;
				const confirmDialog = (this.$root as typeof this.$root & { $confirm?: ConfirmDialogApi })?.$confirm;
				if (!confirmDialog) {
					console.warn(`[ProjectChecklists] leave blocked: confirm dialog host unavailable. projectId=${this.projectId}`);
					return false;
				}
				const result = await confirmDialog.open(
					UNSAVED_CHECKLIST_DIALOG_TEXT.title,
					UNSAVED_CHECKLIST_DIALOG_TEXT.message,
					false,
					UNSAVED_CHECKLIST_DIALOG_TEXT.agreeText,
					UNSAVED_CHECKLIST_DIALOG_TEXT.cancelText,
					false,
					false,
					UNSAVED_CHANGES_DIALOG_OPTIONS
				);
				if (result === false) return true;
				if (result !== true) return false;
				return this.saveAllChecklistDrafts();
			},

			// LSP-22943: the one checklist save sequence, shared by the leave guard's "Save" and the page's Save
			// buttons — new checklist, then renames, then item drafts, stopping at the first that does not save.
			// Each editor shows its own field state or toast; the warns only name which step stopped.
			async saveAllChecklistDrafts(): Promise<boolean> {
				if (!this.hasUnsavedChecklist()) return true;
				const newEditor = this.$refs.newChecklistEditor as InstanceType<typeof ChecklistEditor> | undefined;
				if (this.dirtyEditors.has("new-checklist") && !(await newEditor?.create({ redirectAfterDone: false }))) {
					console.warn(
						`[ProjectChecklists] checklist save stopped: the new checklist did not save. projectId=${this.projectId}, editorMounted=${!!newEditor}`
					);
					return false;
				}
				for (const editor of (this.$refs.renameEditors ?? []) as InstanceType<typeof InlineChecklistEditor>[]) {
					if (this.dirtyEditors.has(`rename:${editor.$props.checklistId}`) && !(await editor.save())) {
						console.warn(
							`[ProjectChecklists] checklist save stopped: a checklist rename did not save. projectId=${this.projectId}, checklistId=${editor.$props.checklistId}`
						);
						return false;
					}
				}
				for (const editor of (this.$refs.itemEditors ?? []) as InstanceType<typeof ChecklistItemEditor>[]) {
					if (this.dirtyEditors.has(`item:${editor.checklistId}`) && !(await editor.saveRecord())) {
						console.warn(
							`[ProjectChecklists] checklist save stopped: an item draft did not save. projectId=${this.projectId}, checklistId=${editor.checklistId}`
						);
						return false;
					}
				}
				await this.$nextTick();
				if (this.hasUnsavedChecklist()) {
					console.warn(
						`[ProjectChecklists] checklist save stopped: drafts still unsaved after every editor saved. projectId=${this.projectId}, dirtyEditors=${[...this.dirtyEditors].join(",")}`
					);
					return false;
				}
				return true;
			},

			async handleChecklistUpdated() {
				await this.loadProjectChecklists();
			},

			handleSelectMediaFromProject(checklistId: string, itemId: string) {
				this.currentChecklistId = checklistId;
				this.currentItemId = itemId;
				this.isExpectingMediaSelection = true;
				// LSP-17252 break-it round 2: bump the generation whenever a flow starts, so an
				// older in-flight handleSelectedMedia (still awaiting its upload call) can tell
				// its own capture is stale once THIS flow becomes the active one.
				this.mediaSelectionGeneration++;

				// Emit event to parent (EntityDetailPage) to start media selection
				this.$emit("select-media-from-project");
			},

			// Reset all media-selection bookkeeping. Routed through by every exit
			// path (empty selection, success, failure, and parent-driven cancel)
			// so the "Add Checklist Item" control reliably re-appears (LSP-16140).
			// Also bumps mediaSelectionGeneration (LSP-17252 break-it round 2) so an
			// in-flight handleSelectedMedia call that started before this reset can
			// recognize itself as stale after its await resolves.
			clearExpectedMediaSelection() {
				this.isExpectingMediaSelection = false;
				this.currentChecklistId = "";
				this.currentItemId = "";
				this.mediaSelectionGeneration++;
			},

			// This method will be called by EntityDetailPage when media is selected
			async handleSelectedMedia(selectedItems: any[]) {
				// Check if we're actually expecting media selection
				if (!this.isExpectingMediaSelection) {
					console.log("Not expecting media selection, ignoring...");
					return;
				}

				if (!selectedItems || selectedItems.length === 0) {
					// LSP-16140: an empty selection silently aborts the checklist attach (no toast).
					// Log it so a future "media never attached" report is traceable. console.warn
					// reaches Datadog; the console.log above does not, per forwardConsoleLogs.
					console.warn("ProjectChecklists.handleSelectedMedia: empty selection, cancelling checklist attach", {
						projectId: this.projectId,
						checklistId: this.currentChecklistId,
						itemId: this.currentItemId
					});
					this.clearExpectedMediaSelection();
					return;
				}

				// LSP-17252 break-it round 2 (updated per post-submit review round): capture which
				// flow this call belongs to before the upload call below — the try block's FIRST
				// await. (There is a SECOND await, loadProjectChecklists(), a few lines down; it
				// intentionally always runs regardless of staleness — see the isStale() comment near
				// the success branch for why.) If the target checklist gets removed (or any other
				// exit starts a fresh flow) while the upload is in flight, mediaSelectionGeneration
				// advances and isStale() flips true. Without this, an old upload's completion
				// (success OR failure) could stomp a NEWER flow's currentChecklistId/currentItemId/
				// isExpectingMediaSelection in its `finally` block — isStale() gates ONLY that reset,
				// never whether the user is told a genuine server outcome.
				const selectionGeneration = this.mediaSelectionGeneration;
				const selectionChecklistId = this.currentChecklistId;
				const selectionItemId = this.currentItemId;
				const isStale = () => this.mediaSelectionGeneration !== selectionGeneration;

				try {
					const mediaItems = selectedItems.map((selectedItem) => {
						let mediaType = selectedItem.type;
						if (selectedItem.type === "recordings") {
							mediaType = "recording";
						} else if (selectedItem.type === "snapshots") {
							mediaType = "snapshot";
						}
						return {
							mediaId: selectedItem.id,
							mediaType: mediaType
						};
					});

					const response = await api.uploadProjectChecklistItemMedia(
						this.projectId,
						selectionChecklistId,
						selectionItemId,
						mediaItems
					);

					// LSP-17252 review round: a stale generation must never swallow a GENUINE server
					// outcome. The upload above actually happened — the media really was attached — so
					// the toast and the list refresh below always run, staleness or not. isStale() only
					// gates the `finally` block's flow-targeting reset (isExpectingMediaSelection/
					// currentChecklistId/currentItemId), because that state may now belong to a
					// different, newer flow; it must never gate whether the user is told the truth or
					// whether the list reflects it. Log when this genuinely-successful attach belongs to
					// an already-superseded flow, purely for traceability — it does not change behavior.
					if (isStale()) {
						console.warn(
							"ProjectChecklists.handleSelectedMedia: an attach succeeded for a flow that was superseded before it resolved — still surfacing the result",
							{
								projectId: this.projectId,
								staleChecklistId: selectionChecklistId,
								staleItemId: selectionItemId,
								currentChecklistId: this.currentChecklistId,
								currentItemId: this.currentItemId
							}
						);
					}

					const { response: responseData } = response;
					const { total, successful, duplicates, failed } = responseData;

					if (successful === total) {
						this.toast.success("Media added to checklist.");
					} else if (duplicates > 0 || failed > 0) {
						this.toast.info(
							`Some selected items are already in the checklist. ${successful} out of ${total} items have been added.`
						);
					}

					await this.loadProjectChecklists();
				} catch (error) {
					// LSP-17252 review round 2 (ls-heroku-ci #1): the success branch above deliberately
					// surfaces a genuine server outcome even when the flow is stale. This branch used to do
					// the opposite — `isStale()` returned early, skipping BOTH the toast AND the
					// `console.error` below. Since mediaSelectionGeneration also bumps when the user simply
					// starts a NEWER selection, a first upload that genuinely failed (500, network) went
					// completely unreported: no toast, and nothing in error tracking but a console.warn
					// without the error object.
					//
					// Split into the two cases that were being conflated, because they are not equivalent:
					//  - target GONE (the flow was cancelled because its checklist/item was removed): the
					//    media has nowhere to land, and a "Failed to add media to checklist" toast naming a
					//    checklist the user just deleted is confusing noise. Suppress the toast only.
					//  - target STILL PRESENT (merely superseded by a newer flow): the attach really did
					//    fail and the user's media is not attached. Staying silent there is the same class
					//    of silent failure this PR exists to remove, so the toast must show.
					// Error tracking now happens in BOTH cases — a genuine server failure is never
					// downgraded to a console.warn just because the UI moved on.
					// LSP-20291: only for a still-active flow. A stale one was cancelled because THIS user removed
					// the checklist (or started another attach), which the LSP-17252 handling below covers.
					// Captured BEFORE the LSP-20291 refresh below: that refresh can itself cancel this flow (an
					// item-level 404 drops the item), and a cancel we caused must not hide this attach's error.
					const wasStaleBeforeRefresh = isStale();
					if (
						!wasStaleBeforeRefresh &&
						selectionChecklistId &&
						(await this.handleChecklistAlreadyDeleted(selectionChecklistId, "add-media", error))
					) {
						return;
					}
					const targetGone = getItemIndex(this.projectChecklists, selectionChecklistId, selectionItemId) < 0;
					if (wasStaleBeforeRefresh) {
						console.warn(
							`ProjectChecklists.handleSelectedMedia: a stale attach failed — ${
								targetGone
									? "its target is gone, so the toast is suppressed"
									: "its target still exists, so the failure is surfaced"
							}`,
							{
								projectId: this.projectId,
								staleChecklistId: selectionChecklistId,
								staleItemId: selectionItemId,
								currentChecklistId: this.currentChecklistId,
								currentItemId: this.currentItemId,
								targetGone
							}
						);
					}
					console.error(error, {
						extra: {
							file: "ProjectChecklists.vue",
							method: "handleSelectedMedia"
						},
						tags: {
							projectId: this.projectId,
							checklistId: selectionChecklistId,
							itemId: selectionItemId,
							selectedItemsCount: selectedItems.length,
							// So a triaged error says which case it was without cross-referencing the warn above.
							wasStale: wasStaleBeforeRefresh,
							targetGone
						}
					});
					// Review round 3 (ls-heroku-ci #1): suppression requires BOTH conditions, not targetGone
					// alone. loadProjectChecklists' list assignment is unguarded (only the cancel decision is
					// sequenced), so an older reload can overwrite projectChecklists with data missing a
					// STILL-ACTIVE flow's target without ever bumping mediaSelectionGeneration. targetGone then
					// reads true while isStale() is false, and gating on targetGone alone swallowed a genuine
					// upload failure for a flow the user is still waiting on — the exact silent-failure class
					// this branch was split apart to prevent, arriving through the non-stale door. The only
					// case that justifies hiding the toast is the one the comment above describes: the flow
					// was cancelled (stale) BECAUSE its target went away.
					if (!wasStaleBeforeRefresh || !targetGone) {
						const errorMessage = getMessageFromError(error);
						this.toast.error(errorMessage || "Failed to add media to checklist.");
					}
				} finally {
					// Only reset if THIS call is still the active flow — clearing here when stale
					// would wipe out whatever newer flow's currentChecklistId/currentItemId/
					// isExpectingMediaSelection are now in place (LSP-17252 break-it round 2).
					if (!isStale()) {
						this.clearExpectedMediaSelection();
					}
				}
			},
			async onItemClick(mediaId: string) {
				this.currentLightBoxId = mediaId;
				this.lightBoxOpen = true;
				await nextTick(() => {
					requestAnimationFrame(() => {
						this.$refs.mediaLightBox?.setCurrentArtifactId(mediaId);
					});
				});
			},
			async checklistFieldFocus() {
				const trimmedSearch = this.checklistSearch?.trim() ?? "";
				if (!this.checklistsLoading && this.availableChecklists.length === 0 && trimmedSearch === "") {
					this.checklistsLoading = true;
					await this.performChecklistSearch(trimmedSearch);
				}
				this.adjustChecklistMenuWidth();
			},
			adjustChecklistMenuWidth() {
				this.$nextTick(() => {
					this.checklistMenuWidth = this.$refs.addChecklistDropdown?.$el.getBoundingClientRect().width + "px";
				});
			},
			closeMenu(conversationId: string) {
				// Close all menus
				(this.$refs.addContentMenu as any[]).forEach((menu) => (menu.isActive = false));
				this.$emit("conversation-created", conversationId);
			},
			filterOutMedia(ids: string[]) {
				// Remove the media item from all submissions in all checklists
				this.projectChecklists.forEach((checklist) => {
					checklist.items.forEach((item) => {
						if (item.submissions) {
							item.submissions = item.submissions.filter((submission) => !ids.includes(submission.mediaId));
						}
					});
				});

				// Remove from mediaResponse
				if (this.mediaResponse && this.mediaResponse.documents) {
					this.mediaResponse.documents = this.mediaResponse.documents.filter(
						(doc) => !ids.includes(doc.id) && !ids.includes(doc.searchResultId ?? doc.id)
					);
				}
			},
			async closeLightBox() {
				this.lightBoxOpen = false;
				this.currentLightBoxId = null;

				if (this.pendingReload) {
					console.log("Pending reload is set, reloading timeline");
					await this.loadMedia(this.searchRequest);
					this.pendingReload = false;
				}
			},
			handleArtifactDeleted(mediaId: string) {
				// Filter out the deleted media from all submissions
				this.filterOutMedia([mediaId]);
				// Close lightbox if it was showing the deleted item
				if (this.currentLightBoxId === mediaId) {
					this.closeLightBox();
				}
			},
			getImageUrl(media: ProjectChecklistItemMedia) {
				return media.thumbnailUrl || media.url;
			},
			async loadMedia(searchRequest: SearchRequest | null) {
				// TODO: Not the ideal way to fetch the media
				// revist the DB query to fetch the media so that the checklists are independent of the entity detail page
				if (!searchRequest) return;

				if (this.lightBoxOpen) {
					this.pendingReload = true;
					return;
				}

				searchRequest.pageSize = 1000;
				this.searchRequest = searchRequest;
				this.mediaResponse = (await api.mediaSearch(searchRequest, this.isAuthenticated)) ?? emptyEntitySearchResponse();
			},
			share(artifactId: string, conversationId: string, artifactType: any) {
				this.$emit("share", artifactId, conversationId, artifactType);
			},
			loadTimeline() {
				this.$emit("load-timeline");
			},
			onMenuToggle(isOpen: boolean) {
				if (isOpen) {
					this.$emit("hide-lucky");
				} else {
					this.$emit("show-lucky");
				}
			},
			hideLucky() {
				this.$emit("hide-lucky");
			},
			showLucky() {
				this.$emit("show-lucky");
			},
			onHandleDragstart(checklistId: string, checklistItemId: string, event: DragEvent) {
				const item = (event.target as HTMLElement).closest<HTMLDivElement>(".project-checklist-item")!;

				const rect = item.getBoundingClientRect();
				const offsetX = event.clientX - rect.left;
				const offsetY = event.clientY - rect.top;
				event.dataTransfer?.setDragImage(item, offsetX, offsetY);

				event.dataTransfer?.setData("project-checklist-item-drag", "");

				this.dragState = {
					checklistId,
					dragId: checklistItemId,
					dropId: null,
					dropAt: null
				};
			},
			onHandleDragend() {
				this.dragState = {
					checklistId: null,
					dragId: null,
					dropId: null,
					dropAt: null
				};
			},
			onHandleTouchstart(checklistId: string, checklistItemId: string) {
				this.dragState = {
					checklistId,
					dragId: checklistItemId,
					dropId: null,
					dropAt: null
				};
			},
			onHandleTouchmove(checklistId: string, event: TouchEvent) {
				const touch = event.touches[0];
				const item = document.elementFromPoint(touch.clientX, touch.clientY)?.closest<HTMLDivElement>(".project-checklist-item");
				if (!item || item.dataset.checklistId !== checklistId) {
					return;
				}

				const rect = item.getBoundingClientRect();
				const offsetY = touch.clientY - rect.top;
				this.dragState = {
					...this.dragState,
					dropId: item.dataset.checklistItemId!,
					dropAt: offsetY < rect.height / 2 ? "top" : "bottom"
				};
			},
			async onHandleTouchend(checklistId: string) {
				try {
					if (this.dragState.checklistId !== checklistId) {
						return;
					}
					await this.reorderDraggedItem(checklistId);
				} finally {
					this.dragState = {
						checklistId: null,
						dragId: null,
						dropId: null,
						dropAt: null
					};
				}
			},
			onHandleTouchcancel() {
				this.dragState = {
					checklistId: null,
					dragId: null,
					dropId: null,
					dropAt: null
				};
			},
			onItemDragover(checklistId: string, checklistItemId: string, event: DragEvent) {
				if (!event.dataTransfer?.types.includes("project-checklist-item-drag") || this.dragState.checklistId !== checklistId) {
					return;
				}
				event.preventDefault();

				const item = (event.target as HTMLElement).closest<HTMLDivElement>(".project-checklist-item")!;

				const rect = item.getBoundingClientRect();
				const offsetY = event.clientY - rect.top;
				this.dragState = {
					...this.dragState,
					dropId: checklistItemId,
					dropAt: offsetY < rect.height / 2 ? "top" : "bottom"
				};
			},
			onPanelDragover(event: DragEvent) {
				if (event.dataTransfer?.types.includes("project-checklist-item-drag")) {
					event.preventDefault();
				}
			},
			async onPanelDrop(checklistId: string, event: DragEvent) {
				if (!event.dataTransfer?.types.includes("project-checklist-item-drag") || this.dragState?.checklistId !== checklistId) {
					return;
				}
				await this.reorderDraggedItem(checklistId);
			},
			async reorderDraggedItem(checklistId: string) {
				const dragIndex = this.dragIndex;
				const dropIndex = this.dropIndex;
				if (dragIndex < 0 || dropIndex < 0 || dragIndex === dropIndex || dragIndex + 1 === dropIndex) {
					return;
				}

				const checklist = this.projectChecklists.find((checklist) => checklist.checklistId === checklistId)!;
				const draggedItem = checklist.items[dragIndex];

				const oldItems = [...checklist.items];
				checklist.items = toSplicedCompat(
					toSplicedCompat(checklist.items, dropIndex, 0, draggedItem),
					dragIndex >= dropIndex ? dragIndex + 1 : dragIndex,
					1
				);

				try {
					await api.reorderProjectChecklistItems(
						this.projectId,
						checklistId,
						checklist.items.map((item) => ({ id: item.id }))
					);
				} catch (error) {
					checklist.items = oldItems;
					if (await this.handleChecklistAlreadyDeleted(checklistId, "reorder", error)) {
						return;
					}
					const errorMessage = "Could not reorder checklist items. Please try again.";
					this.toast.error(errorMessage, { timeout: calculateTimeout(errorMessage) });
					console.error(error, {
						extra: {
							file: "ProjectChecklists.vue",
							method: "onPanelDrop"
						},
						tags: {
							projectId: this.projectId,
							checklistId: checklistId
						}
					});
				}
			},
			showDivider(checklistId: string, itemIndex: number) {
				return (
					checklistId === this.dragState.checklistId &&
					itemIndex !== this.dragIndex &&
					itemIndex !== this.dragIndex + 1 &&
					itemIndex === this.dropIndex
				);
			}
		}
	};
</script>

<style scoped lang="scss">
	@use "vuetify/settings";

	.checklist-status-col {
		width: 100px;
		min-width: 100px;
		flex-shrink: 0;
	}

	.v-expansion-panel::before {
		box-shadow: none;
	}

	.remove-checklist-btn {
		border-color: black !important;
	}

	// LSP-20695 — capped on desktop, but free to use the whole row on sm-and-down (phones and
	// portrait tablets) so the "Pick Checklist (Optional)" label is not truncated.
	// max-width: 100% rather than a wider fixed cap, so the field can never exceed its container.
	// The desktop cap is sized to the label too: at 250px Vuetify's plus + chevron affordances
	// left 164px for a 270px label, so it read "...(Optio" even with the whole row free.
	.add-checklist-dropdown {
		max-width: 360px;
		border-radius: 32px !important;

		@media #{map.get($display-breakpoints, 'sm-and-down')} {
			max-width: 100%;

			// Even at full width a 375px phone leaves ~257px for the label once Vuetify reserves
			// the plus + chevron affordances, and the 16px label measures ~270px. One step down
			// keeps "Pick Checklist (Optional)" whole instead of "...(Optiona".
			:deep(.v-label) {
				font-size: 0.875rem;
			}
		}
	}

	.create-checklist-fixed {
		position: sticky !important;
		bottom: 0;
		z-index: 1;
		background-color: rgb(var(--v-theme-surface));
	}

	.create-checklist-btn:hover::before,
	.create-checklist-fixed:hover::before {
		opacity: 0 !important;
	}

	.submission-content {
		position: relative;
		width: 100%;
		aspect-ratio: 1;
		overflow: hidden;
	}

	.submission-image {
		width: 100%;
		height: 100%;
	}

	.submission-remove-btn {
		position: absolute;
		top: 4px;
		right: 4px;
		background-color: white;
		border: 2px solid black;
		border-radius: 50%;
		width: 24px !important;
		height: 24px !important;
	}

	@media #{map.get($display-breakpoints, 'sm-and-down')} {
		.submission-remove-btn {
			top: 1px;
			right: 1px;
		}
	}

	.expansion-panel-header {
		line-height: 1.2 !important;
	}

	.checklist-name {
		max-width: 70%;
		// LSP-16837: the `text-truncate` utility on the span keeps the name on a single
		// line (nowrap) with an ellipsis. min-width:0 is the load-bearing bit here — a flex
		// item defaults to min-width:auto and won't shrink below its content, so without it a
		// long unbroken name (e.g. no-space text) refuses to shrink and overflows/pushes the
		// row actions on mobile instead of truncating. (The old `text-break` rule was what
		// shredded the name into a narrow column; text-truncate + min-width:0 replaces it.)
		min-width: 0;

		.v-expansion-panel--active & {
			max-width: 50%;
		}
	}

	.checklist-name.checklist-name--ipad-landscape,
	.v-expansion-panel--active .checklist-name.checklist-name--ipad-landscape {
		max-width: none;
		min-width: 0;
		overflow: visible;
		text-overflow: clip;
		white-space: normal !important;
	}

	.checklist-header--ipad-landscape {
		display: contents !important;
	}

	.checklist-header-name-row--ipad-landscape {
		grid-column: 1;
		grid-row: 1;
	}

	.checklist-edit-action--ipad-landscape {
		align-self: center;
		grid-column: 2;
		grid-row: 1;
	}

	.checklist-remove-action--ipad-landscape {
		grid-column: 1 / -1;
		grid-row: 2;
	}

	.expansion-panel-header--ipad-landscape {
		display: grid;
		grid-template-columns: minmax(0, 1fr) auto auto;
		column-gap: 0.5rem;
		row-gap: 0.5rem;
	}

	.expansion-panel-header--ipad-landscape :deep(.v-expansion-panel-title__icon) {
		align-self: center;
		grid-column: 3;
		grid-row: 1;
	}

	// LSP-16645: on small screens (reported in portrait) the fixed 100px count
	// column plus the capped name width squeezed the checklist name out of view.
	// Let the count shrink to its short "X of Y" text (it stays on one line via
	// .text-no-wrap) and lift the name's width cap so it fills the row and
	// truncates with an ellipsis instead of disappearing.
	@media #{map.get($display-breakpoints, 'sm-and-down')} {
		.checklist-status-col {
			width: auto;
			min-width: 0;
		}

		.checklist-name,
		.v-expansion-panel--active .checklist-name {
			max-width: none;
		}
	}

	.disabled-list-item {
		opacity: 0.5;
	}

	.item-label {
		font-size: 0.75rem;
	}
</style>

<style scoped lang="scss">
	.table-header {
		font-size: 0.75rem;
		font-weight: 600 !important;
	}

	// .add-checklist-dropdown sizing lives in the style block above (LSP-20695).

	// Vuetify 2 styles. Keeping in case new look doesn't pass, than will use these as guidelines.
	// .expansion-panel-header {
	// 	padding-left: 24px;
	// 	padding-right: 24px;

	// 	@media screen and (max-width: 959px) {
	// 		padding-left: 8px;
	// 		padding-right: 8px;
	// 	}
	// }

	// .expansion-panel-content {
	// 	::v-deep(.v-expansion-panel-content__wrap) {
	// 		padding-left: 24px;
	// 		padding-right: 24px;

	// 		@media #{map.get($display-breakpoints, 'sm-and-down')} {
	// 			padding-left: 8px;
	// 			padding-right: 8px;
	// 		}
	// 	}
	// }

	.submission-item {
		width: 20%;
		padding: 4px;
		cursor: pointer;

		@media #{map.get($display-breakpoints, 'sm-and-down')} {
			width: 25%;
		}
	}

	.completed-item-checkbox {
		color: rgb(var(--v-theme-primary)) !important;
	}
</style>
